import React, { useState, useEffect } from 'react';
import {
  Alert,
  AlertTitle,
  Button,
  Box,
  Snackbar,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Email as EmailIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import emailVerificationService, { VerificationStatusResponse } from '../services/emailVerification';
import { useAuth } from '../contexts/AuthContext';

interface EmailVerificationBannerProps {
  onDismiss?: () => void;
}

const EmailVerificationBanner: React.FC<EmailVerificationBannerProps> = ({ onDismiss }) => {
  const { user, refreshUser } = useAuth();
  const [verificationStatus, setVerificationStatus] = useState<VerificationStatusResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);

  // Load verification status on mount
  useEffect(() => {
    const loadVerificationStatus = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        const status = await emailVerificationService.getVerificationStatus();
        setVerificationStatus(status);
      } catch (err: any) {
        console.error('Error loading verification status:', err);
      } finally {
        setLoading(false);
      }
    };

    loadVerificationStatus();
  }, [user]);

  // Don't show banner if user is verified or banner is dismissed
  if (!user || dismissed || verificationStatus?.email_verified) {
    return null;
  }

  const handleResendEmail = async () => {
    try {
      setResending(true);
      setError(null);
      
      await emailVerificationService.resendVerification();
      
      // Refresh verification status
      const status = await emailVerificationService.getVerificationStatus();
      setVerificationStatus(status);
      
      setShowSuccess(true);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setResending(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    onDismiss?.();
  };

  const getTimeUntilCanResend = () => {
    // Use backend-calculated remaining_minutes to avoid timezone issues
    return verificationStatus?.remaining_minutes || 0;
  };

  const formatLocalTime = (utcTimestamp: string) => {
    // Convert UTC timestamp to local time for display
    const date = new Date(utcTimestamp + 'Z'); // Add 'Z' to ensure UTC parsing
    return date.toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  const minutesUntilResend = getTimeUntilCanResend();

  if (loading) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={16} />
          Cargando estado de verificación...
        </Box>
      </Alert>
    );
  }

  return (
    <>
      <Alert 
        severity="warning" 
        sx={{ mb: 2 }}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={handleDismiss}
            startIcon={<CloseIcon />}
          >
            Cerrar
          </Button>
        }
      >
        <AlertTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon />
            Email no verificado
          </Box>
        </AlertTitle>
        
        <Box sx={{ mb: 2 }}>
          Tu dirección de correo <strong>{verificationStatus?.email}</strong> aún no ha sido verificada.
          Revisa tu bandeja de entrada y haz clic en el enlace de verificación.
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleResendEmail}
            disabled={resending || !verificationStatus?.can_resend}
            startIcon={resending ? <CircularProgress size={16} /> : <RefreshIcon />}
          >
            {resending ? 'Enviando...' : 'Reenviar Email'}
          </Button>

          {minutesUntilResend && minutesUntilResend > 0 && (
            <Chip
              label={`Espera ${minutesUntilResend} min`}
              size="small"
              color="warning"
              variant="outlined"
            />
          )}

          {verificationStatus?.verification_sent_at && (
            <Chip
              label={`Último envío: ${formatLocalTime(verificationStatus.verification_sent_at)}`}
              size="small"
              variant="outlined"
            />
          )}
        </Box>
      </Alert>

      {/* Success snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowSuccess(false)} severity="success">
          Email de verificación enviado. Revisa tu bandeja de entrada.
        </Alert>
      </Snackbar>
    </>
  );
};

export default EmailVerificationBanner;
